import {
  type ConsoleLogLevel,
  type Log,
  type LoggerAPI,
  type LoggerProps,
  LogLevel,
  type LogWithLevel,
  type ModuleLoggerAPI,
} from './types';

const mapLogLevelToConsoleLogLevel = (logLevel: LogLevel): ConsoleLogLevel | undefined => {
  const logLevelString: Record<string, ConsoleLogLevel> = {
    [LogLevel.DEBUG]: 'debug',
    [LogLevel.ERROR]: 'error',
    [LogLevel.INFO]: 'info',
    [LogLevel.WARN]: 'warn',
  };

  return logLevelString[logLevel];
};

export const Logger = ({ tag, minLevel = LogLevel.DEBUG }: LoggerProps): LoggerAPI => {
  const moduleLoggerList = new Map<string, ModuleLoggerAPI>();
  const log: LogWithLevel = (module, message, level, props, style) => {
    if (!level) {
      if (props) {
        // biome-ignore lint: <explanation>
        console.log(`[${tag}][${module}]:`, message, props);
      } else {
        // biome-ignore lint: <explanation>
        console.log(`[${tag}][${module}]:`, message);
      }
      return;
    }

    if (level <= minLevel) {
      const logLevelString = mapLogLevelToConsoleLogLevel(level);

      if (props) {
        if (style) {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console[logLevelString ?? 'log'](`%c [${tag}][${module}]:`, style, message, props);
          return;
        }
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console[logLevelString ?? 'log'](`[${tag}][${module}]:`, message, props);
      } else {
        if (style) {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console[logLevelString ?? 'log'](`%c [${tag}][${module}]:`, style, message);
          return;
        }
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console[logLevelString ?? 'log'](`[${tag}][${module}]:`, message);
      }
    }
  };

  const createLog =
    (logLevel: LogLevel): Log =>
    (module, message, props, style) => {
      log(module, message, logLevel, props, style);
    };

  const error: Log = createLog(LogLevel.ERROR);
  const warn: Log = createLog(LogLevel.WARN);
  const info: Log = createLog(LogLevel.INFO);
  const debug: Log = createLog(LogLevel.DEBUG);

  const getModuleLogger = (module: string): ModuleLoggerAPI => {
    let moduleLogger = moduleLoggerList.get(module);

    const infoLogStyle = 'background: honeyDew; color: green';
    const debugLogStyle = 'background: aliceBlue; color: steelBlue';

    if (!moduleLogger) {
      moduleLogger = {
        log: (...rest) => log(module, ...rest),
        error: (message, props) => createLog(LogLevel.ERROR)(module, message, props),
        warn: (message, props) => createLog(LogLevel.WARN)(module, message, props),
        info: (message, props) => createLog(LogLevel.INFO)(module, message, props, infoLogStyle),
        debug: (message, props) => createLog(LogLevel.DEBUG)(module, message, props, debugLogStyle),
      };
      moduleLoggerList.set(module, moduleLogger);
    }

    return moduleLogger;
  };

  return {
    getModuleLogger,
    log,
    error,
    warn,
    info,
    debug,
  };
};

const logger = Logger({
  tag: 'UTILS',
  minLevel: LogLevel.DEBUG,
});

export const getModuleLogger = logger.getModuleLogger;

export const LoggerService = ((): { getLogger: () => LoggerAPI } => {
  return {
    getLogger: (): LoggerAPI => logger,
  };
})();
